/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import moment from 'moment';
import { DatePicker, Title } from '@cambridgeassessment/cambridge-ui';
import { BusinessUnit, convertToMidnight, Series, Roles } from '@cambridgeassessment/lib-ee-commons';
import { useDispatch, useSelector } from 'react-redux';
import thunks from '../../../../store/series/thunks';
import { StyledInputContainer } from '../Series.styles';
import { SubmissionWindow } from '../../../../types/Series';
import { StyledParagraph, StyledSubtitle } from './index.styles';
import { RootState } from '../../../../store';
import { currentTime } from '../../../../util/time';
import { Authorized } from 'lib-browser';

interface Props extends SubmissionWindow {
  seriesId: Series['_id'];
  end: string | undefined;
  businessUnitId?: BusinessUnit['id'];
}

const DISPLAY_FORMAT = 'DD/MM/YYYY';
const DEFAULT_DAYS = 30;
const tomorrow = currentTime().add(1, 'day');

const SeriesDetails: React.FC<Props> = ({ name, buffer, end, seriesId }) => {
  const dispatch = useDispatch();
  const {
    series: { seriesDetails },
    userData: { selectedRole },
  } = useSelector((state: RootState) => state);

  const selectedMarksWindow = seriesDetails?.marksSubmissionWindows?.find((serie) => serie.name === name) ?? null;
  const latestMarksDate =
    seriesDetails?.marksEndDates[name] && seriesDetails?.marksEndDates[name].find((date) => Math.max.apply(date));
  const latestWorkDate =
    seriesDetails?.workEndDates[name] && seriesDetails?.workEndDates[name].find((date) => Math.max.apply(date));
  const [errorMessages, setErrorMessages] = useState<Record<string, string>>({});

  const isChosenDateValid = (fieldName: string, value: moment.Moment): boolean => {
    const seriesStartDateForComparing = moment(seriesDetails?.startDate).add(2, 'days').toISOString();
    const midnightValue = convertToMidnight(value);
    if (midnightValue < convertToMidnight(tomorrow)) {
      setErrorMessages((state) => ({ ...state, [fieldName]: 'Must be a date in the future' }));
      return true;
    }
    if (fieldName === 'marksBuffer' && midnightValue < seriesStartDateForComparing) {
      setErrorMessages((state) => ({
        ...state,
        [fieldName]: 'Must be at least two days later than series start date',
      }));
      return true;
    }
    if (fieldName === 'marksBuffer' && buffer && midnightValue > buffer) {
      setErrorMessages((state) => ({ ...state, [fieldName]: 'Cannot be later than the work submission date' }));
      return true;
    }
    if (fieldName === 'marksBuffer' && latestMarksDate && latestMarksDate >= midnightValue) {
      setErrorMessages((state) => ({ ...state, [fieldName]: 'Must be later than the marks submission date' }));
      return true;
    }
    if (fieldName === 'moderatorAccess' && buffer && midnightValue < convertToMidnight(moment(buffer))) {
      setErrorMessages((state) => ({ ...state, [fieldName]: 'Must be later than the work submission date' }));
      return true;
    }
    if (
      fieldName === 'buffer' &&
      selectedMarksWindow?.buffer &&
      midnightValue < convertToMidnight(moment(selectedMarksWindow?.buffer))
    ) {
      setErrorMessages((state) => ({
        ...state,
        [fieldName]: 'Cannot be before marks submission buffer date in case the series includes moderated components.',
      }));
      return true;
    }
    if (fieldName === 'buffer' && latestWorkDate && latestWorkDate >= midnightValue) {
      setErrorMessages((state) => ({
        ...state,
        [fieldName]: 'Cannot be earlier than the latest advertised works deadline for a component in the series.',
      }));
      return true;
    }
    if (
      fieldName === 'buffer' &&
      selectedMarksWindow &&
      convertToMidnight(moment(selectedMarksWindow.moderatorAccessDate)) < midnightValue
    ) {
      setErrorMessages((state) => ({ ...state, [fieldName]: 'Cannot be later than the moderator access date' }));
      return true;
    }
    setErrorMessages((state) => ({ ...state, [fieldName]: '' }));
    return false;
  };

  const updateWorkSubmissionBufferDate = (date: moment.Moment): void => {
    const newDate = date.parseZone();
    const notValid = isChosenDateValid('buffer', newDate);
    if (notValid) {
      return;
    }
    dispatch(thunks.changeBufferDate(seriesId, newDate, name, 'buffer'));
  };

  const updateMarkSubmissionBufferDate = (date: moment.Moment): void => {
    const newDate = date.parseZone();
    const notValid = isChosenDateValid('marksBuffer', newDate);
    if (notValid) {
      return;
    }
    dispatch(thunks.changeBufferDate(seriesId, newDate, name, 'marksBuffer'));
  };

  const updateModeratorAccessDate = (date: moment.Moment): void => {
    const newDate = date.parseZone();
    const notValid = isChosenDateValid('moderatorAccess', newDate);
    if (notValid) {
      return;
    }
    dispatch(thunks.changeBufferDate(seriesId, newDate, name, 'moderatorAccess'));
  };

  const RenderComponent = (props: {
    readOnly?: boolean;
    onChange: (date: moment.Moment) => void;
    helper?: boolean;
    bufferDate?: string;
    fieldName: string;
  }) => {
    const value = props.bufferDate
      ? moment.parseZone(props.bufferDate).toISOString(true)
      : moment.parseZone(end).add(DEFAULT_DAYS, 'days').toISOString(true);
    return (
      <StyledInputContainer aria-label={`${name}-${props.fieldName}`}>
        <DatePicker
          onChange={props.onChange}
          value={value}
          format={DISPLAY_FORMAT}
          dataQaId={`${name}-${props.fieldName}`}
          disabled={props.readOnly}
          helperText={props.helper ? 'Not applicable to examined components' : ''}
          errorMsg={errorMessages[props.fieldName]}
        />
      </StyledInputContainer>
    );
  };

  return (
    <>
      <Title data-qa-id={`selected-series-submission-${name}`}>{name}</Title>
      <StyledParagraph data-qa-id={`selected-series-submission-${name}-info`}>
        Change the dates in which the system closes for marks or work/sample submissions; and for moderators to access
        marks and samples. Note these dates are not displayed in the system. Deadlines shown are taken from Component
        Business Rules.
      </StyledParagraph>
      <Authorized
        selectedRole={selectedRole}
        roles={[Roles.SUBMIT_SUPER_ADMIN]}
        readOnlyRoles={[Roles.SUBMIT_COMPLIANCE, Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER, Roles.SUBMIT_ADMINISTRATOR]}
      >
        <StyledSubtitle>Marks submission buffer date</StyledSubtitle>
        <RenderComponent
          onChange={updateMarkSubmissionBufferDate}
          helper
          bufferDate={selectedMarksWindow?.buffer}
          fieldName='marksBuffer'
        />
        <StyledSubtitle>Work submission buffer date</StyledSubtitle>
        <RenderComponent onChange={updateWorkSubmissionBufferDate} bufferDate={buffer} fieldName='buffer' />
        <StyledSubtitle>Moderator access date</StyledSubtitle>
        <RenderComponent
          onChange={updateModeratorAccessDate}
          helper
          bufferDate={selectedMarksWindow?.moderatorAccessDate}
          fieldName='moderatorAccess'
        />
      </Authorized>
    </>
  );
};

export default SeriesDetails;
